import type { ReactNode } from 'react';
import type { AxiosError } from 'axios';
import type { UseMutationResult } from '@tanstack/react-query';
import { type TwoFAParams } from '@/shared/types';

export type MutationHookResult<
  TParams = unknown,
  TResponse = unknown,
  TError = AxiosError,
> = UseMutationResult<TResponse, TError, TParams>;

export type MutationHook<
  TParams = unknown,
  TResponse = unknown,
  TError = AxiosError,
> = (options?: {
  onSuccess?: (data: TResponse) => void;
  onError?: (error: TError) => void;
}) => MutationHookResult<TParams, TResponse, TError>;

export type OperationMFAViewData = {
  view: OperationMFAViewState;
  description?: ReactNode;
} | null;

export type OperationMFAVerificationOptions<
  TParams = unknown,
  TResponse = unknown,
  TError extends AxiosError = AxiosError,
> = {
  useMutationHook: MutationHook<TParams, TResponse, TError>;
  initialParams: TParams;
  injectMFA: (params: TParams, mfa: TwoFAParams) => TParams;
  requireMFASetup?: boolean;
  onSuccess?: (data: TResponse) => void;
  onError?: (error: TError) => void;
  onCancel?: () => void;
  onBack?: () => void;
  onClose?: () => void;
  onMFASetupComplete?: () => void;
  onViewChange?: (view?: OperationMFAViewData) => void;
};

export const OPERATION_MFA_VIEW_TYPE = {
  SETUP_REQUIRED: 'setup-required',
  METHOD_SELECTION: 'method-selection',
  CONFIRMATION: 'confirmation',
} as const;

export type OperationMFAViewState =
  (typeof OPERATION_MFA_VIEW_TYPE)[keyof typeof OPERATION_MFA_VIEW_TYPE];

export type OperationMFAStatus = {
  viewState: OperationMFAViewState;
  isLoading: boolean;
  isReady: boolean;
  isSubmitting: boolean;
  hasError: boolean;
  errorMessage?: string;
};
